import { Classes } from "@blueprintjs/core";
import React, { useCallback } from "react";
import styled from "styled-components";

import { HexButton, HexKeyCombo } from "../../hex-components";
import { useDialog } from "../../util/dialogs";
import { HotKeys } from "../../util/hotkeys";
import { trackCounterEvent } from "../../util/trackEvent";
import { SearchIcon } from "../icons/CustomIcons";

const SearchButtonContainer = styled.div<{ $isNavItem: boolean }>`
  display: flex;
  align-items: center;
  height: 100%;
  flex: 1;

  cursor: pointer;

  justify-content: center;

  .${Classes.BUTTON} {
    flex: 1;
    max-width: 500px;
    padding-left: 10px;

    .${Classes.BUTTON_TEXT} {
      justify-content: center;
    }
  }

  &:hover {
    &&&& .${Classes.BUTTON} {
      background-color: ${({ theme }) => theme.hoverColor};
    }
    .${Classes.BUTTON_TEXT} {
      color: ${({ $isNavItem, theme }) =>
        $isNavItem ? theme.fontColor.DEFAULT : theme.fontColor.MUTED};
      .${Classes.KEY_COMBO} {
        border: 1px solid ${({ theme }) => theme.borderColor.DEFAULT};
      }
    }
  }
`;

const SearchButton = styled(HexButton)<{ $isNavItem: boolean }>`
  display: flex;
  height: 28px;
  padding-left: 10px;
  align-items: center;

  .${Classes.BUTTON_TEXT} {
    display: flex;
    gap: 10px;
    align-items: center;
    justify-content: space-between;

    color: ${({ $isNavItem, theme }) =>
      $isNavItem ? theme.fontColor.DEFAULT : theme.fontColor.MUTED};
  }

  @media (max-width: ${({ theme }) => `${theme.mediaQuery.EXTRA_SMALL}px`}) {
    gap: 0 !important;
  }
`;

const StyledHexKeyCombo = styled(HexKeyCombo)`
  > .${Classes.ICON}:first-child:last-child {
    margin: 0 1px 0 0;
  }
`;

const SearchPrompt = styled.span`
  gap: 4px;
  align-items: center;
  display: flex;
  flex: 1;
  justify-content: space-between;

  @media (max-width: ${({ theme }) => `${theme.mediaQuery.EXTRA_SMALL}px`}) {
    display: none;
  }
`;

interface WorkspaceSearchButtonProps {
  location?: "popover_sidebar" | "home_sidebar";
  textOverride?: string;
  iconOnly?: boolean;
  minimal?: boolean;
  isNavItem?: boolean;
}

export const WorkspaceSearchButton: React.FunctionComponent<
  WorkspaceSearchButtonProps
> = ({
  iconOnly = false,
  isNavItem = false,
  location,
  minimal = false,
  textOverride,
}) => {
  const { openDialog: openWorkspaceSearchDialog } =
    useDialog("workspace-search");

  const toggleWorkspaceSearch = useCallback(() => {
    if (location === "popover_sidebar") {
      trackCounterEvent("popover_sidebar.workspace_search.click");
    } else if (location === "home_sidebar") {
      trackCounterEvent("home_sidebar.workspace_search.click");
    }
    trackCounterEvent("workspace_search.open_dialog");
    openWorkspaceSearchDialog();
  }, [location, openWorkspaceSearchDialog]);

  return (
    <SearchButtonContainer
      $isNavItem={isNavItem}
      onClick={toggleWorkspaceSearch}
    >
      <SearchButton
        $isNavItem={isNavItem}
        alignText="left"
        fill={!location}
        icon={<SearchIcon />}
        minimal={minimal}
        subtle={true}
      >
        {!iconOnly && (
          <SearchPrompt>
            {textOverride || "Search your workspace..."}
            <div css="margin: -1px -4px 0 0;">
              {minimal ? (
                <NoBorderHexKeyCombo combo={HotKeys.QUICK_SEARCH_ALT} />
              ) : (
                <StyledHexKeyCombo combo={HotKeys.QUICK_SEARCH_ALT} />
              )}
            </div>
          </SearchPrompt>
        )}
      </SearchButton>
    </SearchButtonContainer>
  );
};

const NoBorderHexKeyCombo = styled(StyledHexKeyCombo)`
  && {
    border: 1px solid ${({ theme }) => theme.backgroundColor.DEFAULT};
  }
`;
